import { useCallback, useEffect, useRef, useState } from "react";
import { friendlyError } from "./crm";

export interface AsyncState<T> {
  data: T | undefined;
  error: Error | null;
  loading: boolean;
  reload: () => void;
}

/** Runs a loader and keeps loading, error and data together. Stale responses are ignored. */
export function useAsync<T>(load: () => Promise<T>, deps: unknown[]): AsyncState<T> {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const run = useRef(0);

  useEffect(() => {
    const id = ++run.current;
    let alive = true; // a response after the screen has gone is dropped (it used to update a removed component)
    setLoading(true);
    setError(null);
    load().then(
      (d) => {
        if (!alive || id !== run.current) return;
        setData(d);
        setLoading(false);
      },
      (e: unknown) => {
        if (!alive || id !== run.current) return;
        setError(friendlyError(e));
        setLoading(false);
      },
    );
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the caller's deps decide when to load again
  }, [...deps, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, error, loading, reload };
}
